import { IconButton, LuIcon } from "@overmap-ai/blocks"
import * as turf from "@turf/turf"
import type { LineString } from "geojson"
import { memo, useCallback, useId, useMemo } from "react"
import type { MarkerDragEvent, MarkerEvent } from "react-map-gl/mapbox"

import { modelIconMapping } from "../../mappings/icons"
import { LINE_PAINT } from "../constants"
import { MapSlotLayerId } from "../enums"
import { Layer, Marker, Source } from "./memo"

interface LineStringEditorProps {
	lineString: LineString
	onLineStringChange: (lineString: LineString) => void
}

export const LineStringEditor = memo<LineStringEditorProps>((props) => {
	const { lineString, onLineStringChange } = props

	const id = useId()

	const centerPoint = useMemo(() => {
		return turf.centroid(turf.bboxPolygon(turf.bbox(lineString)))
	}, [lineString])

	const points = useMemo(() => {
		return turf.explode(lineString).features.map((feature) => feature.geometry)
	}, [lineString])

	const midpointPoints = useMemo(() => {
		return points.slice(0, -1).map((point, pointIndex) => {
			return turf.midpoint(point, points[pointIndex + 1]).geometry
		})
	}, [points])

	const handleMove = useCallback(
		(e: MarkerDragEvent) => {
			const { lngLat } = e

			const from = centerPoint
			const to = turf.point(lngLat.toArray())

			const distance = turf.distance(from, to)
			const bearing = turf.bearing(from, to)

			onLineStringChange(turf.transformTranslate(lineString, distance, bearing))
		},
		[centerPoint, lineString, onLineStringChange],
	)

	const handlePointDrag = useCallback(
		(e: MarkerDragEvent, pointIndex: number) => {
			const { lngLat } = e

			const newPoint = turf.point(lngLat.toArray()).geometry

			const newPoints = points.map((point, index) => {
				return index === pointIndex ? newPoint : point
			})

			const newLineString = turf.lineString(newPoints.map((point) => point.coordinates)).geometry

			onLineStringChange(newLineString)
		},
		[points, onLineStringChange],
	)

	const handleAddPoint = useCallback(
		(e: MarkerEvent<MouseEvent>, pointIndex: number) => {
			const { originalEvent } = e

			originalEvent.stopPropagation()

			const newPoints = [
				...points.slice(0, pointIndex + 1),
				midpointPoints[pointIndex],
				...points.slice(pointIndex + 1),
			]

			const newLineString = turf.lineString(newPoints.map((point) => point.coordinates)).geometry

			onLineStringChange(newLineString)
		},
		[midpointPoints, onLineStringChange, points],
	)

	return (
		<>
			<Source id={`${id}-source`} type="geojson" data={lineString}>
				<Layer id={`${id}-line`} paint={LINE_PAINT} type="line" beforeId={MapSlotLayerId.DRAW} />
			</Source>

			<Marker
				anchor="center"
				longitude={centerPoint.geometry.coordinates[0]}
				latitude={centerPoint.geometry.coordinates[1]}
				draggable
				onDrag={handleMove}
			>
				<IconButton size="xs" radius="full" accentColor="info" className="flex!">
					<LuIcon icon={modelIconMapping.move} />
				</IconButton>
			</Marker>

			{midpointPoints.map((point, pointIndex) => (
				<Marker
					key={`midpoint-${pointIndex}`}
					anchor="center"
					longitude={point.coordinates[0]}
					latitude={point.coordinates[1]}
					onClick={(e) => {
						handleAddPoint(e, pointIndex)
					}}
				>
					<IconButton
						size="xs"
						radius="full"
						accentColor="info"
						className="flex! size-4! border border-white"
					>
						<LuIcon icon={modelIconMapping.add} />
					</IconButton>
				</Marker>
			))}

			{points.map((point, pointIndex) => (
				<Marker
					key={pointIndex}
					anchor="center"
					longitude={point.coordinates[0]}
					latitude={point.coordinates[1]}
					draggable
					onDrag={(e) => handlePointDrag(e, pointIndex)}
				>
					<IconButton
						size="xs"
						radius="full"
						accentColor="info"
						className="block! size-3! border border-white"
					/>
				</Marker>
			))}
		</>
	)
})
LineStringEditor.displayName = "LineStringEditor"
